import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import React, { createContext, type FC, type ReactNode, useContext } from 'react';
import { Query } from '../types';

export const SUPPRESS_GLOBAL_LOADING_META = { suppressGlobalLoading: true } as const;
export const SUPPRESS_GLOBAL_LOADING_WHEN_CACHED_META = {
  suppressGlobalLoadingWhenCached: true,
} as const;

const NON_BLOCKING_QUERY_KEYS = new Set<Query>([
  Query.ShareHistory,
  Query.FunctionMembers,
  Query.PostcodeLookup,
]);

type Props = {
  isLoading: boolean;
};

const UIContext = createContext<Props>({} as Props);

export const UiProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const isFetching = useIsFetching({
    predicate: (query) =>
      query.meta?.suppressGlobalLoading !== true &&
      !(query.meta?.suppressGlobalLoadingWhenCached === true && query.state.data !== undefined) &&
      !NON_BLOCKING_QUERY_KEYS.has(query.queryKey[0] as Query),
  });
  const isMutating = useIsMutating({
    predicate: (mutation) => mutation.options.meta?.suppressGlobalLoading !== true,
  });
  const isLoading = isFetching > 0 || isMutating > 0;

  const context: Props = {
    isLoading,
  };

  return <UIContext.Provider value={context}>{children}</UIContext.Provider>;
};

export const useUiContext = () => {
  const context = useContext(UIContext);
  return context;
};
